import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { UiOmniboxTrigger } from '@packages/types';
import { useAuth } from '../../lib/auth/useAuth';
import { useCopy } from '../../hooks/useCopy';
import { useInvoicingVisible } from '../../hooks/useInvoicingVisible';
import { isInsideModKScope, isModK, modKShortcutLabel } from '../../lib/mod-k-shortcut';
import { track, useTrackOnChange, useTrackOutcome } from '../../lib/ui-usage';
import { loadDashboard, loadHub, loadInvoiceDetail } from '../../lib/route-loaders';
import { prefetchChunk } from '../../lib/stale-chunk';
import { askBillState } from '../chat/ask-bill';
import { SearchFieldTrigger } from '../ui/SearchFieldTrigger';
import { SearchPopover } from '../ui/SearchPopover';
import { SparkleIcon } from '../ui/SparkleIcon';
import { OmniboxInvoiceTrailing, OmniboxKindIcon } from './omnibox.icons';
import { omniboxItemPath, parseOmniboxItemId, toPopoverItems } from './omnibox.items';
import { positionBucket, queryLengthBucket, rankInGroup } from './omnibox.usage';
import { useOmniboxDestinations } from './useOmniboxDestinations';
import { useOmniboxRecents } from './useOmniboxRecents';
import { useOmniboxSearch } from './useOmniboxSearch';

/** Phone variant on a tablet-width screen: the anchored panel replaces the field. */
const OVERLAY_WIDTH_PX = 480;
/** Desktop panel: wider than the 380px field, centred under it. */
const DESKTOP_PANEL_WIDTH_CLASS = 'w-[552px] xl:w-[632px] max-w-[calc(100vw-1rem)]';
const DESKTOP_PANEL_GAP_PX = 8;

/**
 * "Search or ask Bill": one field that finds clients, matters and invoices and,
 * on Enter, hands the typed text to the dashboard chat as a new question. Both
 * the desktop top bar and the phone header mount one; only the visible one
 * answers Cmd/Ctrl+K.
 *
 * Desktop: the field stays put while open and takes the typing itself (the
 * popover hosts its input in the field), with the results panel centred under
 * it. Phone: the popover replaces the field - a bottom sheet on a handset.
 */
export function GlobalOmnibox({ variant }: { variant: 'desktop' | 'phone' }) {
  const { accountId, user } = useAuth();
  if (!accountId || !user) return null;
  return <OmniboxField accountId={accountId} userId={user.id} variant={variant} />;
}

function OmniboxField({
  accountId,
  userId,
  variant,
}: {
  accountId: string;
  userId: string;
  variant: 'desktop' | 'phone';
}) {
  const copy = useCopy();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const invoicesVisible = useInvoicingVisible() === true;
  const [open, setOpen] = useState(false);
  // Counts opens, so one query typed in two different opens is two searches.
  const [attempt, setAttempt] = useState(0);
  const [text, setText] = useState('');
  const closedOutcome = useTrackOutcome('ui-usage.omnibox.closed');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [inputHost, setInputHost] = useState<HTMLDivElement | null>(null);
  const desktop = variant === 'desktop';

  const trimmed = text.trim();
  const search = useOmniboxSearch({ accountId, text, open, invoicesVisible });
  const recents = useOmniboxRecents({ accountId, userId, open: open && trimmed === '', invoicesVisible });
  const destinations = useOmniboxDestinations({ text: trimmed });
  const { groups: recordGroups, isLoading, isError } = trimmed === '' ? recents : search;
  // "Go to" is local and instant, so it leads: the list does not shift when the search lands.
  const groups = destinations.group ? [destinations.group, ...recordGroups] : recordGroups;
  const items = toPopoverItems(
    groups,
    (kind) => <OmniboxKindIcon kind={kind} />,
    (row) => (row.invoice ? <OmniboxInvoiceTrailing {...row.invoice} /> : null),
  );

  const queryLength = queryLengthBucket(trimmed.length);
  const recordsShown = recordGroups.some((group) => group.rows.length > 0) ? 'some' : 'none';

  // An empty answer to the query as typed; never a failed search or a stale one.
  const searchedEmpty = open && trimmed !== '' && search.settled && search.groups.length === 0;
  useTrackOnChange(
    'ui-usage.omnibox.search-returned-empty',
    searchedEmpty ? { queryLength } : null,
    `${attempt}:${trimmed}`,
  );

  function openOmnibox(trigger: UiOmniboxTrigger) {
    setOpen(true);
    setAttempt((count) => count + 1);
    closedOutcome.start();
    track('ui-usage.omnibox.opened', { trigger });
  }
  const openFromShortcut = useEffectEvent(() => openOmnibox('shortcut'));

  // Also runs after a selection or Ask, which settled the attempt first; the first settle wins.
  function close() {
    closedOutcome.settle({ outcome: 'dismissed', recordsShown, queryLength });
    setOpen(false);
    setText('');
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!isModK(e) || isInsideModKScope(e.target)) return;
      if (open) {
        e.preventDefault();
        return;
      }
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      // The hidden instance (display: none) has no boxes, so only the visible one opens.
      if (!triggerRef.current || triggerRef.current.getClientRects().length === 0) return;
      e.preventDefault();
      openFromShortcut();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Warm the destination chunks once per open so a result opens without a wait.
  useEffect(() => {
    if (!open) return;
    void prefetchChunk(loadHub);
    void prefetchChunk(loadDashboard);
    if (invoicesVisible) void prefetchChunk(loadInvoiceDetail);
  }, [open, invoicesVisible]);

  function handleSelect(id: string) {
    const target = parseOmniboxItemId(id);
    if (!target) return;
    const position = positionBucket(rankInGroup(items.map((item) => item.id), id));
    if (target.kind === 'destination') {
      const destination = destinations.destinationFor(target.id);
      if (!destination) return;
      closedOutcome.settle({ outcome: 'destination-selected', destination: destination.category, position, queryLength });
      navigate(destination.to);
      return;
    }
    const source = trimmed !== '' ? 'search' : recents.fromHistory[target.kind] ? 'recent' : 'suggested';
    closedOutcome.settle({ outcome: 'record-selected', kind: target.kind, source, position, queryLength });
    navigate(omniboxItemPath(target.kind, target.id));
  }

  function askBill() {
    closedOutcome.settle({ outcome: 'ask', recordsShown, queryLength });
    navigate('/dashboard', { state: askBillState(trimmed, 'omnibox'), replace: pathname === '/dashboard' });
  }

  const fieldLabel = copy('omnibox:fieldLabel');
  let emptyLabel: string = copy('omnibox:nothingYet');
  if (isError) emptyLabel = copy('omnibox:searchFailed');
  else if (trimmed !== '') {
    emptyLabel = copy(invoicesVisible ? 'omnibox:noMatches' : 'omnibox:noMatchesWithoutInvoices', {
      query: trimmed,
    });
  }

  const kbdClass =
    'shrink-0 rounded border border-border bg-bg-primary px-1.5 py-0.5 font-sans text-xs text-text-muted';

  return (
    <div className="relative">
      <SearchFieldTrigger
        triggerRef={triggerRef}
        open={open}
        onClick={() => openOmnibox('click')}
        ariaLabel={fieldLabel}
        ariaKeyShortcuts="Meta+K Control+K"
        appearance={desktop ? 'raised' : 'compact'}
        className={desktop ? 'relative z-30 h-9 shadow-sm' : 'h-10'}
        contentClassName="flex min-w-0 items-center gap-2 truncate text-text-muted"
        accessory={
          desktop ? (
            <kbd aria-hidden="true" className={kbdClass}>
              {modKShortcutLabel()}
            </kbd>
          ) : null
        }
      >
        <SparkleIcon className="h-4 w-4 shrink-0 text-primary" />
        <span className="truncate">{fieldLabel}</span>
      </SearchFieldTrigger>
      {desktop && (
        // The open field, laid over the (invisible) trigger. It is the popover's
        // input host, so a press anywhere on it (padding, icon, "esc") is not an
        // outside click. The icon and hint are absolute so the portaled input is
        // the only flow child. It sits above the popover's whole-window scrim, so
        // the field is the one thing in the header left undimmed.
        <div
          ref={setInputHost}
          className={`absolute inset-0 z-[9999] items-center rounded-lg border border-primary bg-bg-primary pl-9 pr-14 shadow-sm ring-3 ring-primary/15 [&>div]:min-w-0 [&>div]:flex-1 ${open ? 'flex' : 'hidden'}`}
        >
          <SparkleIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
          <kbd aria-hidden="true" className={`${kbdClass} absolute right-3 top-1/2 -translate-y-1/2`}>
            {copy('omnibox:escKey')}
          </kbd>
        </div>
      )}
      {open && (
        <SearchPopover
          triggerRef={triggerRef}
          onClose={close}
          searchValue={text}
          onSearchChange={setText}
          items={items}
          onSelect={handleSelect}
          isLoading={isLoading}
          emptyLabel={emptyLabel}
          placeholder={fieldLabel}
          inputAriaLabel={fieldLabel}
          listAriaLabel={copy('omnibox:resultsLabel')}
          inputLeadingIcon={<SparkleIcon className="h-4 w-4" />}
          appearance="rich"
          listMaxHeightClass="max-h-[min(640px,calc(100dvh-6rem))]"
          {...(desktop
            ? {
                anchorMode: 'below' as const,
                widthClass: DESKTOP_PANEL_WIDTH_CLASS,
                panelAlign: 'center' as const,
                panelGapPx: DESKTOP_PANEL_GAP_PX,
                scrimTopPx: 0,
                searchInputHost: inputHost,
              }
            : { anchorMode: 'overlay' as const, widthPx: OVERLAY_WIDTH_PX })}
          pinnedAction={
            trimmed === ''
              ? undefined
              : {
                  label: copy('omnibox:ask.label', { query: trimmed }),
                  sublabel: copy('omnibox:ask.hint'),
                  icon: <SparkleIcon className="h-4 w-4" />,
                  enterHint: copy('omnibox:ask.enter'),
                  onSelect: askBill,
                  inKeyboardCycle: true,
                }
          }
        />
      )}
    </div>
  );
}
